import { Button, GridCellText, type TableColumn } from '@civilgrid/ui/components';
import { OPERATOR_TYPES } from '@civilgrid/ui/utils';
import { DateTime } from 'luxon';
import type { ExportEscalation } from '~/api/internal';
import { EXPORT_HISTORY_TAB_ID } from '~/pages/projectDetails/exportTabIds';

export const getExportEscalationColumns = (
  onView: (escalation: ExportEscalation) => void,
): TableColumn<ExportEscalation>[] => [
  {
    id: 'projectName',
    header: 'Project',
    size: 200,
    accessorKey: 'projectName',
    cell: ({ row }) => {
      const { projectId, projectName } = row.original;
      const link = `${window.location.origin}/projects/${projectId}/${EXPORT_HISTORY_TAB_ID}`;

      return (
        <GridCellText>
          <a
            href={link}
            target="_blank"
            rel="noopener noreferrer"
            className="text-primary-dark underline flex flex-row items-center"
            data-testid={`export-escalations-project-link-${row.original.id}`}
          >
            {projectName}
          </a>
        </GridCellText>
      );
    },
  },
  {
    id: 'requestorName',
    header: 'Requestor',
    size: 180,
    accessorFn: (row) => row.requestorName ?? row.requestorEmail,
    cell: ({ getValue }) => <GridCellText>{getValue<string>()}</GridCellText>,
  },
  {
    id: 'formats',
    header: 'Formats',
    size: 150,
    accessorFn: (row) => row.formats.join(', '),
    cell: ({ getValue }) => <GridCellText>{getValue<string>()}</GridCellText>,
    enableSorting: false,
  },
  {
    id: 'requestedAt',
    header: 'Requested At',
    size: 130,
    accessorKey: 'requestedAt',
    cell: ({ getValue }) => {
      const value = getValue<string>();
      return <GridCellText>{value ? DateTime.fromISO(value).toLocaleString(DateTime.DATETIME_MED) : '-'}</GridCellText>;
    },
  },
  {
    id: 'escalationStatus',
    header: 'Status',
    size: 120,
    accessorKey: 'escalationStatus',
    cell: ({ getValue, row }) =>
      row.original.isRunning ? (
        <GridCellText>Running…</GridCellText>
      ) : (
        <GridCellText className="capitalize">{getValue<string>()}</GridCellText>
      ),
    meta: {
      filterOperatorTypeConfig: OPERATOR_TYPES.EXACT,
      filterValueConfig: {
        type: 'select',
        options: [
          { value: 'pending', label: 'Pending' },
          { value: 'completed', label: 'Completed' },
          { value: 'archived', label: 'Archived' },
        ],
      },
    },
  },
  {
    id: 'actions',
    header: 'Actions',
    size: 80,
    enableSorting: false,
    cell: ({ row }) => (
      <Button
        variant="secondary"
        size="small"
        onClick={() => onView(row.original)}
        data-testid={`export-escalations-view-${row.original.id}`}
      >
        View
      </Button>
    ),
  },
];
